/**
 * Smart Intake invite link card.
 * Displays a copyable signup link that tax pros can share with clients.
 * Rendered only for businesses on the intake frontend (gated by the caller).
 */
import { useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CopyButton,
  Group,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconCheck, IconCopy, IconLink } from "@tabler/icons-react";
import { useAuthStore } from "@utils/stores/authStore";
import { gradientTextStyle } from "@/utils/brandStyles";
import { getIntakeBaseUrl } from "@/utils/intakeUrl";

interface SmartIntakeInviteLinkProps {
  /** Compact mode hides the description text. */
  compact?: boolean;
}

export const SmartIntakeInviteLink = ({
  compact = false,
}: SmartIntakeInviteLinkProps) => {
  const businessInfo = useAuthStore((state) => state.businessInfo);
  const businessId = businessInfo?.businessId;
  const businessName = businessInfo?.businessName;
  const vanitySlug = businessInfo?.vanitySlug;

  const inviteLink = useMemo(() => {
    if (!businessId) return "";
    const intakeBaseUrl = getIntakeBaseUrl();
    // Prefer the short vanity-slug URL when the business has one set
    // — it's what the tax pro actually wants to share.
    if (vanitySlug) return `${intakeBaseUrl}/i/${vanitySlug}`;
    // Fallback keeps existing clipboard/SMS links working for businesses
    // that haven't customized their slug yet.
    const params = new URLSearchParams({ businessId });
    if (businessName) params.set("businessName", businessName);
    return `${intakeBaseUrl}/create-account?${params.toString()}`;
  }, [businessId, businessName, vanitySlug]);

  const handleCopyNotification = useCallback(() => {
    // Visual feedback is handled by CopyButton — no additional notification needed
  }, []);

  if (!businessId) return null;

  return (
    <Card
      shadow="sm"
      padding={compact ? "sm" : "md"}
      radius="md"
      withBorder
      style={{ borderColor: "#D0E7FF", background: "#f2fbff" }}
    >
      <Stack gap={compact ? "xs" : "sm"}>
        <Group gap="xs">
          <IconLink size={20} color="#0077CC" />
          <Text fw={600} size={compact ? "sm" : "md"}>
            <span style={gradientTextStyle()}>Smart Intake</span> Invite Link
          </Text>
        </Group>

        {!compact && (
          <Text size="sm" c="dimmed">
            Share this link with your clients so they can create an account and
            upload their tax documents.
          </Text>
        )}

        <CopyButton value={inviteLink} timeout={2000}>
          {({ copied, copy }) => (
            <Tooltip
              label={copied ? "Copied!" : "Click to copy"}
              withArrow
              position="bottom"
            >
              <UnstyledButton
                onClick={() => {
                  copy();
                  handleCopyNotification();
                }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: `1px solid ${copied ? "#16a34a" : "#D0E7FF"}`,
                  background: copied ? "#f0fdf4" : "white",
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Box style={{ overflow: "hidden" }}>
                    <Text
                      size="sm"
                      fw={500}
                      c={copied ? "green" : "#0077CC"}
                      truncate
                    >
                      {inviteLink}
                    </Text>
                  </Box>
                  {copied ? (
                    <IconCheck size={18} color="#16a34a" />
                  ) : (
                    <IconCopy size={18} color="#0077CC" />
                  )}
                </Group>
              </UnstyledButton>
            </Tooltip>
          )}
        </CopyButton>
      </Stack>
    </Card>
  );
};

export default SmartIntakeInviteLink;
